import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, Circle, Eye, EyeOff, Loader2, Mail, User } from "lucide-react";
import { AuthShell, PrimaryButton, inputCls } from "@/components/AuthShell";
import { api } from "@/lib/api";

const MIN_LEN = 10;
const RULES = [
  [`At least ${MIN_LEN} characters`, (p) => p.length >= MIN_LEN],
  ["Includes a number", (p) => /\d/.test(p)],
  ["Includes a capital letter", (p) => /[A-Z]/.test(p)],
];
const TABBED = ["franchisee_employee", "machine_owner", "rental_customer", "prospect"];
const loginRoute = (role) => `/?tab=${TABBED.includes(role) ? role : "franchisee"}`;

const DeadEnd = ({ title, body }) => {
  const navigate = useNavigate();
  return (
    <div data-testid="setup-deadend">
      <h1 className="font-display text-3xl font-extrabold uppercase">{title}</h1>
      <p className="mt-3 text-sm leading-6 text-neutral-400">{body}</p>
      <PrimaryButton data-testid="deadend-login" onClick={() => navigate("/")}>GO TO SIGN IN</PrimaryButton>
      <button data-testid="deadend-forgot" onClick={() => navigate("/forgot-password")} className="mt-3 w-full rounded-lg border border-[#333] py-3.5 text-[13px] font-bold tracking-wider text-white hover:border-[#4CCC06]">
        GET A NEW LINK
      </button>
    </div>
  );
};

export default function SetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = (params.get("token") || "").trim();
  const [phase, setPhase] = useState(token ? "verifying" : "missing");
  const [dead, setDead] = useState(null);
  const [info, setInfo] = useState(null);
  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [fullName, setFullName] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) return;
    api(`/auth/setup-password/${encodeURIComponent(token)}`, { auth: false })
      .then((r) => { setInfo(r); setPhase("valid"); })
      .catch((e) => { setDead({ status: e.status, text: typeof e.detail === "string" ? e.detail : "" }); setPhase("expired"); });
  }, [token]);

  const strong = RULES.every(([, fn]) => fn(pwd));
  const match = confirm.length > 0 && pwd === confirm;
  const nameOk = !info?.needs_name || fullName.trim().length >= 2;
  const canSubmit = strong && match && nameOk && !busy;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    setErr(null);
    try {
      await api(`/auth/setup-password/${encodeURIComponent(token)}`, {
        method: "POST",
        auth: false,
        body: { new_password: pwd, full_name: info?.needs_name ? fullName.trim() : undefined },
      });
      setDone(true);
      setTimeout(() => navigate(loginRoute(info?.role), { replace: true }), 1500);
    } catch (e2) {
      setErr(e2.message || "Could not set password. The link may have expired.");
      if (e2.status === 410) setPhase("expired");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell>
      <p className="mb-4 text-[10px] font-bold tracking-[0.3em] text-[#4CCC06]">ADVANCED LASER RESTORATION · FRANCHISEE PORTAL</p>
      {phase === "missing" && <DeadEnd title="Setup link missing" body="This page expects a setup token in the URL. Open the link from the email we sent you, or contact your administrator." />}
      {phase === "verifying" && <div className="flex items-center gap-3 py-8 text-neutral-400" data-testid="setup-verifying"><Loader2 className="animate-spin text-[#4CCC06]" /> Checking your invitation…</div>}
      {phase === "expired" && (
        <DeadEnd
          title={dead?.status === 409 ? "You're already set up" : "Link expired or invalid"}
          body={dead?.text || 'This link is no longer valid. Use "Forgot password?" on the sign-in screen to get a fresh link in seconds.'}
        />
      )}
      {phase === "valid" && info && !done && (
        <form onSubmit={submit} data-testid="setup-form">
          <h1 className="font-display text-3xl font-extrabold uppercase">{info.context === "reset" ? "Choose a new password" : "Welcome to ALR"}</h1>
          <p className="mt-2 text-sm text-neutral-400">{info.context === "reset" ? "Set a new password for your account below." : "Set a password to activate your account."}</p>
          <div className="mt-4 space-y-1 text-sm text-neutral-300">
            <p className="flex items-center gap-2" data-testid="setup-email"><Mail size={15} className="text-neutral-500" />{info.email}</p>
            {info.full_name && <p className="flex items-center gap-2"><User size={15} className="text-neutral-500" />{info.full_name}</p>}
          </div>
          <div className="my-5 h-px bg-[#333]" />
          {info.needs_name && (
            <label className="mb-4 block">
              <span className="mb-1.5 block text-[11px] font-bold tracking-[0.2em] text-[#4CCC06]">YOUR FULL NAME</span>
              <input data-testid="setup-fullname-input" className={inputCls} value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="First and last name" />
            </label>
          )}
          <span className="mb-1.5 block text-[11px] font-bold tracking-[0.2em] text-[#4CCC06]">NEW PASSWORD</span>
          <div className="relative">
            <input data-testid="setup-password-input" type={show ? "text" : "password"} autoComplete="new-password" className={`${inputCls} pr-12`} value={pwd} onChange={(e) => setPwd(e.target.value)} placeholder="At least 10 characters, 1 number, 1 capital" />
            <button type="button" data-testid="setup-toggle-password" onClick={() => setShow((v) => !v)} className="absolute inset-y-0 right-0 flex w-12 items-center justify-center text-neutral-400">{show ? <EyeOff size={18} /> : <Eye size={18} />}</button>
          </div>
          <span className="mb-1.5 mt-4 block text-[11px] font-bold tracking-[0.2em] text-[#4CCC06]">CONFIRM PASSWORD</span>
          <input data-testid="setup-password-confirm" type={show ? "text" : "password"} autoComplete="new-password" className={inputCls} value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Re-enter your password" />
          <ul className="mt-4 space-y-1.5 text-[13px]" data-testid="setup-strength-hints">
            {[...RULES, ["Passwords match", () => match]].map(([label, fn]) => {
              const ok = fn(pwd);
              return <li key={label} className={`flex items-center gap-2 ${ok ? "text-[#4CCC06]" : "text-neutral-500"}`}>{ok ? <CheckCircle2 size={15} /> : <Circle size={15} />}{label}</li>;
            })}
          </ul>
          {err && <p data-testid="setup-error" className="mt-3 text-[13px] text-[#FF3333]">{err}</p>}
          <PrimaryButton type="submit" data-testid="setup-password-submit" disabled={!canSubmit}>
            {busy ? <Loader2 className="animate-spin" size={18} /> : "SET PASSWORD & CONTINUE"}
          </PrimaryButton>
          <p className="mt-3 text-center text-[11px] text-neutral-500">This link expires in 24 hours and can only be used once.</p>
        </form>
      )}
      {done && (
        <div className="flex flex-col items-center py-6 text-center" data-testid="setup-success">
          <CheckCircle2 size={56} className="text-[#4CCC06]" />
          <h2 className="mt-3 font-display text-2xl font-extrabold uppercase">Password set</h2>
          <p className="text-sm text-neutral-400">Taking you to the login screen…</p>
        </div>
      )}
    </AuthShell>
  );
}
